import React, { useState, useEffect } from "react";
import { IonCard, IonCardContent, IonIcon, IonInput, IonButton } from "@ionic/react";
import Jimp from "jimp";
import { closestColor } from "../helpers/helpers";
import TrashIcon from "../../../assets/Converter/Trash.svg";
import ColorFillIcon from "../../../assets/SvgEditor/ColorFill.svg";
import "../index.css";
import { isEmbroideryTrace } from "../../../config/appVariant";

const UnifiedLayerPanel = ({ 
  colors, 
  setColors,
  file, 
  imageDimensions,
  showLayerPreviews = true,
  onLayerVisibilityChange,
  crop,
  compact = false,
  vertical = false,
  verticalCompact = false,
  hideHexCodes = false,
  onAddNewClick = null,
}) => {
  const [layerPreviews, setLayerPreviews] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [focusedInputs, setFocusedInputs] = useState({});
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Generate individual layer previews
  const generateLayerPreviews = async () => {
    if (!file || !colors.length || !showLayerPreviews) return;

    setIsGenerating(true);
    try {
      const image = await Jimp.read(file);
      let processedImage = image;
      
      // Apply crop if provided
      if (crop && crop.width && crop.height && crop.x !== null && crop.y !== null) {
        processedImage = image.crop(crop.x, crop.y, crop.width, crop.height);
      }
      
      const width = processedImage.getWidth();
      const height = processedImage.getHeight();
      const previews = [];

      // Convert colors to format expected by helper functions
      const COLORS = colors.map(color => {
        const hex = color[0].replace('#', '');
        return [
          parseInt(hex.slice(0, 2), 16),
          parseInt(hex.slice(2, 4), 16),
          parseInt(hex.slice(4, 6), 16)
        ];
      });

      // Create preview for each color layer
      for (let layerIndex = 0; layerIndex < colors.length; layerIndex++) {
        const layerImage = new Jimp(width, height, 0x00000000); // Transparent background

        // Process each pixel
        for (let x = 0; x < width; x++) {
          for (let y = 0; y < height; y++) {
            const rgba = Jimp.intToRGBA(processedImage.getPixelColor(x, y));
            if (rgba.a < 25) continue; // Skip transparent pixels

            const pixelColorRGB = closestColor(
              processedImage.getPixelColor(x, y),
              COLORS,
              colors.length
            );

            // Check if this pixel belongs to the current layer
            if (
              pixelColorRGB.r === COLORS[layerIndex][0] &&
              pixelColorRGB.g === COLORS[layerIndex][1] &&
              pixelColorRGB.b === COLORS[layerIndex][2]
            ) {
              // Set pixel to the original color
              const originalColor = Jimp.rgbaToInt(
                COLORS[layerIndex][0],
                COLORS[layerIndex][1],
                COLORS[layerIndex][2],
                255
              );
              layerImage.setPixelColor(originalColor, x, y);
            }
          }
        }

        // Convert to base64 for display
        const base64 = await new Promise((resolve) => {
          layerImage.getBase64(Jimp.MIME_PNG, (err, src) => {
            resolve(src);
          });
        });

        previews.push({
          layerIndex,
          color: colors[layerIndex][0],
          preview: base64,
          name: `Layer ${layerIndex + 1}`
        });
      }

      setLayerPreviews(previews);
    } catch (error) {
      console.error('Error generating layer previews:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (showLayerPreviews && file && colors.length > 0) {
      generateLayerPreviews();
    }
  }, [file, colors, showLayerPreviews, crop]);



  const updateColor = (index, newValue) => {
    const updatedColors = colors.map((item, idx) =>
      idx === index ? [newValue, item[1]] : item
    );
    setColors(updatedColors);
  };

  const removeColor = (index) => {
    const updatedColors = colors.filter((_, colorIndex) => index !== colorIndex);
    setColors(updatedColors);
  };

  const addNewColor = () => {
    if (onAddNewClick) {
      onAddNewClick();
    } else {
      setColors((prev) => [...prev, ["", true]]);
    }
  };

  const setInputFocus = (index, focused) => {
    setFocusedInputs(prev => ({
      ...prev,
      [index]: focused
    }));
  };

  if (!colors.length) {
    return null;
  }

  // Ultra-compact vertical layout for narrow sidebar
  if (verticalCompact) {
    return (
      <div style={{ 
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        width: "100%",
      }}>
        {colors.map((colorData, index) => (
          <div
            key={index}
            style={{
              position: "relative",
              width: "64px",
              height: "64px",
              borderRadius: "8px",
              overflow: "hidden",
              flexShrink: 0,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: "2px solid white",
              transition: "transform 0.2s, box-shadow 0.2s",
              transform: hoveredIndex === index ? "scale(1.05)" : "scale(1)",
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: `#${colorData[0]}`,
              }}
            />
            {hoveredIndex === index && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeColor(index);
                  }}
                  style={{
                    backgroundColor: "white",
                    border: "none",
                    borderRadius: "6px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#c33",
                  }}
                >
                  <IonIcon icon={TrashIcon} style={{ fontSize: "16px" }} />
                </button>
              </div>
            )}
          </div>
        ))}
        
        {/* Add New Color Button */}
        <button
          onClick={addNewColor}
          style={{
            width: "64px",
            height: "64px",
            backgroundColor: "white",
            border: "2px dashed #e83e8c",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
            transition: "all 0.2s",
            color: "#e83e8c",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#fff5f9";
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "white";
            e.target.style.transform = "scale(1)";
          }}
        >
          <IonIcon icon={ColorFillIcon} style={{ fontSize: "24px" }} />
        </button>
      </div>
    );
  }

  // Vertical sidebar layout for detected colors
  if (vertical) {
    return (
      <div style={{ 
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
      }}>
        {colors.map((colorData, index) => (
          <div
            key={index}
            title={isEmbroideryTrace ? `Thread color ${index + 1}` : undefined}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              backgroundColor: "white",
              border: focusedInputs[index] ? "2px solid #5282FF" : "2px solid #E2E2E2",
              borderRadius: "8px",
              padding: "10px",
              fontSize: "12px",
              fontFamily: "Clash Grotesk",
              flexShrink: 0,
              transition: "border-color 0.2s",
            }}
          >
            {/* Color swatch and hex input */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor: `#${colorData[0]}`,
                  borderRadius: "6px",
                  border: "2px solid #979797",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1 }}>
                <IonInput
                  style={{
                    width: "100%",
                    fontSize: "13px",
                    fontWeight: 600,
                    fontFamily: "Clash Grotesk",
                    "--padding-start": "6px",
                    "--padding-end": "6px",
                  }}
                  aria-label={`${isEmbroideryTrace ? "Thread color" : "Color"} ${index + 1} hex code`}
                  value={colorData[0]}
                  onIonChange={(e) => updateColor(index, e.target.value)}
                  onIonFocus={() => setInputFocus(index, true)}
                  onIonBlur={() => setInputFocus(index, false)}
                />
              </div>
            </div>
            
            {/* Delete button */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                onClick={() => removeColor(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "transparent",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  fontSize: "11px",
                  fontFamily: "Clash Grotesk",
                  fontWeight: 500,
                  color: "#666",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#fee";
                  e.target.style.borderColor = "#f88";
                  e.target.style.color = "#c33";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.borderColor = "#ddd";
                  e.target.style.color = "#666";
                }}
              >
                <IonIcon icon={TrashIcon} style={{ fontSize: "12px" }} />
                Remove
              </button>
            </div>
          </div>
        ))}
        
        {/* Add New Color Button */}
        <button
          onClick={addNewColor}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            backgroundColor: "white",
            border: "2px dashed #e83e8c",
            borderRadius: "8px",
            padding: "12px",
            fontSize: "13px",
            fontFamily: "Clash Grotesk",
            fontWeight: 600,
            color: "#e83e8c",
            cursor: "pointer",
            flexShrink: 0,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#fff5f9";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "white";
          }}
        >
          <IonIcon icon={ColorFillIcon} style={{ fontSize: "16px" }} />
          {isEmbroideryTrace ? "Add thread color" : "New Color"}
        </button>
      </div>
    );
  }

  // Compact horizontal layout for color swatches.
  // display:contents so each chip becomes a direct flex item of the parent
  // bar, allowing the whole set (chips + Edit Colors + Apply) to wrap together.
  if (compact) {
    return (
      <div style={{ display: "contents" }}>
        {colors.map((colorData, index) => {
          const isFocused = focusedInputs[index];
          const isHovered = hoveredIndex === index;
          return (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#fff",
                border: isFocused ? "1.5px solid #5282FF" : "1.5px solid #E6E6EA",
                borderRadius: "8px",
                padding: hideHexCodes ? "3px" : "3px 4px 3px 5px",
                fontFamily: "Clash Grotesk",
                flexShrink: 0,
                height: "30px",
                boxShadow: isFocused
                  ? "0 0 0 3px rgba(82,130,255,0.14)"
                  : "0 1px 2px rgba(0,0,0,0.04)",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            >
              {/* Swatch — checkerboard backing so light/near-white colors stay visible */}
              <div
                style={{
                  position: "relative",
                  width: "20px",
                  height: "20px",
                  borderRadius: "5px",
                  flexShrink: 0,
                  backgroundImage:
                    "linear-gradient(45deg, #d9d9e0 25%, transparent 25%), linear-gradient(-45deg, #d9d9e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d9d9e0 75%), linear-gradient(-45deg, transparent 75%, #d9d9e0 75%)",
                  backgroundSize: "8px 8px",
                  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: `#${colorData[0]}`,
                    borderRadius: "5px",
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.16)",
                  }}
                />
              </div>
              {!hideHexCodes && (
                <div style={{ display: "flex", alignItems: "center", gap: "1px" }}>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#B0B0B8",
                      fontFamily: "'SF Mono', ui-monospace, Menlo, monospace",
                    }}
                  >
                    #
                  </span>
                  <IonInput
                    style={{
                      width: "56px",
                      fontSize: "12px",
                      fontWeight: 600,
                      letterSpacing: "0.3px",
                      textTransform: "uppercase",
                      color: "#3A3A42",
                      fontFamily: "'SF Mono', ui-monospace, Menlo, monospace",
                      "--padding-start": "0px",
                      "--padding-end": "0px",
                      "--padding-top": "0px",
                      "--padding-bottom": "0px",
                    }}
                    aria-label={`${isEmbroideryTrace ? "Thread color" : "Color"} ${index + 1} hex code`}
                    value={colorData[0]}
                    onIonChange={(e) => updateColor(index, e.target.value)}
                    onIonFocus={() => setInputFocus(index, true)}
                    onIonBlur={() => setInputFocus(index, false)}
                  />
                </div>
              )}
              <button
                aria-label={`Remove ${isEmbroideryTrace ? "thread color" : "color"} #${colorData[0]}`}
                onClick={() => removeColor(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "22px",
                  height: "22px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  flexShrink: 0,
                  padding: 0,
                  backgroundColor: isHovered ? "#FDECEF" : "transparent",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#FDECEF";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <IonIcon
                  icon={TrashIcon}
                  style={{
                    fontSize: "13px",
                    color: isHovered ? "#E0245E" : "#A8A8B0",
                    transition: "color 0.15s",
                  }}
                />
              </button>
            </div>
          );
        })}

        {/* Edit Colors Button */}
        <button
          onClick={addNewColor}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            backgroundColor: "#fff",
            border: "1.5px dashed #e83e8c",
            borderRadius: "8px",
            padding: "0 10px",
            fontSize: "12px",
            fontFamily: "Clash Grotesk",
            fontWeight: 600,
            color: "#e83e8c",
            cursor: "pointer",
            flexShrink: 0,
            height: "30px",
            transition: "background-color 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#FFF1F7";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#fff";
          }}
        >
          <IonIcon icon={ColorFillIcon} style={{ fontSize: "13px" }} />
          {isEmbroideryTrace ? "Add thread color" : "Edit Colors"}
        </button>
      </div>
    );
  }

  // Default vertical layout
  return (
    <div style={{ 
      width: "100%", 
      padding: "8px",
      display: "flex",
      flexDirection: "column",
    }}>
      {isGenerating && showLayerPreviews && (
        <div style={{
          textAlign: "center",
          padding: "20px",
          fontSize: "12px",
          color: "#666"
        }}>
          Generating layer previews...
        </div>
      )}

      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}>
        {colors.map((colorData, index) => {
          const layerPreview = layerPreviews.find(preview => preview.layerIndex === index);
          
          return (
                         <IonCard
               key={index}
               style={{
                 margin: 0,
                 boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                 border: focusedInputs[index] ? "2px solid #5282FF" : "2px solid #E2E2E2",
                 borderRadius: "8px",
                 flexShrink: 0,
                 minHeight: showLayerPreviews ? "auto" : "54px"
               }}
            >
              <IonCardContent style={{ padding: "8px" }}>
                                 {/* Color Selection Row */}
                 <div style={{
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "space-between",
                   height: "46px",
                   marginBottom: showLayerPreviews && layerPreview ? "8px" : "0px"
                 }}>
                   <div
                     style={{
                       width: "16px",
                       height: "19px",
                       backgroundColor: `#${colorData[0]}`,
                       borderRadius: "2px",
                       marginRight: "8px",
                       border: "1px solid #979797",
                       flexShrink: 0
                     }}
                   />
                   <IonInput
                     style={{
                       flexGrow: 1,
                       fontFamily: "sans-serif",
                       fontSize: "14px",
                       marginRight: "8px"
                     }}
                     aria-label={`${isEmbroideryTrace ? "Thread color" : "Color"} ${index + 1} hex code`}
                     value={colorData[0]}
                     onIonChange={(e) => updateColor(index, e.target.value)}
                     onIonFocus={() => setInputFocus(index, true)}
                     onIonBlur={() => setInputFocus(index, false)}
                   />
                   
                   <IonIcon
                     icon={TrashIcon}
                     style={{
                       fontSize: "10px",
                       color: "#888",
                       cursor: "pointer",
                       padding: "4px"
                     }}
                     onClick={() => removeColor(index)}
                   />
                 </div>
                
                                 {/* Layer Preview (if enabled) */}
                 {showLayerPreviews && layerPreview && (
                   <div style={{
                     width: "100%",
                     height: "60px",
                     backgroundColor: "#f5f5f5",
                     borderRadius: "4px",
                     overflow: "hidden",
                     border: "1px solid #ddd",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     flexShrink: 0
                   }}>
                     <img
                       src={layerPreview.preview}
                       alt={`Layer ${index + 1} preview`}
                       style={{
                         maxWidth: "100%",
                         maxHeight: "100%",
                         objectFit: "contain",
                         display: "block"
                       }}
                     />
                   </div>
                 )}
              </IonCardContent>
            </IonCard>
          );
        })}
      </div>

      {/* Add New Color Button - Fixed at bottom */}
      <div style={{ flexShrink: 0, marginTop: "8px" }}>
        <IonButton
          expand="block"
          fill="outline"
          style={{
            "--border-color": "#e83e8c",
            "--border-style": "dashed",
            "--border-width": "2px",
            "--color": "#525252",
            "--background": "#fff",
            "--background-hover": "white",
            "--background-activated": "white",
            height: "45px",
            fontWeight: 500,
            textTransform: "none",
            width: "100%",
            margin: "0",
          }}
          onClick={addNewColor}
        >
          <IonIcon icon={ColorFillIcon} style={{ marginRight: "6px" }} />
          {isEmbroideryTrace ? "Add thread color" : "New Color"}
        </IonButton>
      </div>
    </div>
  );
};

export default UnifiedLayerPanel;
